import { CalendarClock, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useCancelTrialBooking } from '~entities/student/api/cancel-trial-booking';
import {
  type TrialStudent,
  useGetAllTrialStudents,
} from '~entities/student/api/get-all-trial-students';

import {
  filterTrialBookings,
  type TrialBookingView,
} from '~features/classes/lib/class-trial-bookings';
import { TrialRescheduleDialog } from '~features/classes/ui/trial-reschedule-dialog';

import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

export function ClassTrialBookingsDialog({
  classId,
  className,
  open,
  onOpenChange,
  onViewClass,
  onBookTrial,
  onConfigureBookingLink,
}: {
  classId: string;
  className: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewClass?: () => void;
  onBookTrial?: () => void;
  onConfigureBookingLink?: () => void;
}) {
  const [view, setView] = useState<TrialBookingView>('upcoming');
  const [rescheduleTrial, setRescheduleTrial] = useState<TrialStudent | null>(
    null,
  );
  const { formatInStudioTz } = useStudioDateFormatter();
  const cancelMutation = useCancelTrialBooking();
  const { data, isLoading, isError } = useGetAllTrialStudents({
    classId,
    limit: 100,
    dateField: 'startTime',
  });
  const today = formatInStudioTz(new Date(), 'yyyy-MM-dd');
  const rows = filterTrialBookings(data?.data ?? [], view, today, (startTime) =>
    formatInStudioTz(startTime, 'yyyy-MM-dd'),
  );

  const cancelTrial = async (trial: TrialStudent) => {
    try {
      await cancelMutation.mutateAsync({ trialId: trial._id });
      toast.success('Trial cancelled.');
    } catch {
      toast.error('We could not cancel this trial. Please try again.');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='max-w-4xl'>
          <DialogHeader>
            <DialogTitle>Trials booked</DialogTitle>
            <DialogDescription>
              {className}. Times are shown in the studio timezone.
            </DialogDescription>
          </DialogHeader>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <Select
              value={view}
              onValueChange={(value) => {
                if (value === 'upcoming' || value === 'all') setView(value);
              }}
            >
              <SelectTrigger className='w-40'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='upcoming'>Upcoming</SelectItem>
                <SelectItem value='all'>All bookings</SelectItem>
              </SelectContent>
            </Select>
            <div className='flex gap-2'>
              {onViewClass ? (
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={onViewClass}
                >
                  View class
                </Button>
              ) : null}
              {onConfigureBookingLink ? (
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={onConfigureBookingLink}
                >
                  Configure booking link
                </Button>
              ) : null}
              {onBookTrial ? (
                <Button type='button' size='sm' onClick={onBookTrial}>
                  Book a trial
                </Button>
              ) : null}
            </div>
          </div>
          {isLoading ? <p className='text-sm'>Loading trials…</p> : null}
          {isError ? (
            <p className='text-sm text-red-600'>Trials could not be loaded.</p>
          ) : null}
          {!isLoading && !isError && rows.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No trials in this view.
            </p>
          ) : null}
          {rows.length > 0 ? (
            <div className='max-h-96 overflow-auto rounded-md border'>
              <table className='w-full min-w-[700px] text-left text-sm'>
                <thead className='sticky top-0 bg-muted'>
                  <tr>
                    <th className='px-3 py-2 font-medium'>Student</th>
                    <th className='px-3 py-2 font-medium'>Parent</th>
                    <th className='px-3 py-2 font-medium'>Scheduled</th>
                    <th className='px-3 py-2 font-medium'>Status</th>
                    <th className='px-3 py-2 text-right font-medium'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((trial) => {
                    const scheduled =
                      trial.appointmentStatus === 'Trial Scheduled';
                    return (
                      <tr key={trial._id} className='border-t'>
                        <td className='px-3 py-2 font-medium'>
                          {trial.studentName ||
                            `${trial.firstName} ${trial.lastName}`.trim() ||
                            'Student'}
                        </td>
                        <td className='px-3 py-2'>
                          {trial.name || trial.email || 'Parent unavailable'}
                        </td>
                        <td className='px-3 py-2'>
                          {formatInStudioTz(
                            trial.startTime,
                            'MMM d, yyyy h:mm a',
                          ) || 'Time unavailable'}
                        </td>
                        <td className='px-3 py-2'>{trial.appointmentStatus}</td>
                        <td className='px-3 py-2'>
                          {scheduled ? (
                            <div className='flex justify-end gap-1'>
                              <Button
                                type='button'
                                size='sm'
                                variant='outline'
                                onClick={() => {
                                  setRescheduleTrial(trial);
                                }}
                              >
                                <CalendarClock className='mr-1 size-4' />
                                Reschedule
                              </Button>
                              <Button
                                type='button'
                                size='sm'
                                variant='outline'
                                className='text-red-600'
                                disabled={cancelMutation.isPending}
                                onClick={() => void cancelTrial(trial)}
                              >
                                {cancelMutation.isPending ? (
                                  <Loader2 className='mr-1 size-4 animate-spin' />
                                ) : (
                                  <XCircle className='mr-1 size-4' />
                                )}
                                Cancel
                              </Button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <TrialRescheduleDialog
        trial={rescheduleTrial}
        open={rescheduleTrial != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setRescheduleTrial(null);
        }}
      />
    </>
  );
}
